'use client'

import { useTranslations } from 'next-intl'

import { Button } from '@graftcode/design-system'

import type { Notification } from '../../types/notification.type'
import { NotificationCardIcon } from '../NotificationCardIcon'

import styles from './NotificationCard.module.scss'

type Props = {
  notification: Notification
  onAction?: (notificationId: string, actionId: string) => void
}

export function NotificationCard({ notification, onAction }: Props) {
  const t = useTranslations('notifications')

  return (
    <article className={styles.card}>
      <div className={styles.content}>
        <div className={styles.header}>
          <NotificationCardIcon type={notification.type} />
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{t(notification.titleKey)}</h3>
            {!notification.isRead ? <span className={styles.unreadDot} aria-label={t('a11y.unreadIndicator')} /> : null}
          </div>
        </div>

        <div className={styles.body}>
          <p className={styles.description}>
            {notification.description.map((segment) => (
              <span
                key={`${notification.id}-${segment.text}-${segment.emphasis ? 'emphasis' : 'plain'}`}
                className={segment.emphasis ? styles.emphasis : undefined}
              >
                {segment.text}
              </span>
            ))}
          </p>

          {notification.actions && notification.actions.length > 0 ? (
            <div className={styles.actions}>
              {notification.actions.map((action) => (
                <Button
                  key={action.id}
                  size="m"
                  variant={action.variant === 'primary' ? 'primary' : 'outlined'}
                  onClick={() => onAction?.(notification.id, action.id)}
                >
                  {t(action.labelKey)}
                </Button>
              ))}
            </div>
          ) : null}

          <p className={styles.timestamp}>{notification.timestamp}</p>
        </div>
      </div>
    </article>
  )
}
